
import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { Building, EntityQuery, Filter, LandUseI18N, LocalGovernment, Parcel, PostalCode, Query } from "@the-real-insight/tri-model";
import { userAPI } from "../UserAPI";
import SocioDemographicsProperties from "./plot-of-land/SocioDemographicsProperties";
import DecarbonizationProperties from "./plot-of-land/DecarbonisationProperties";
import PoIProperties from "./plot-of-land/PoIProperties";
import * as turf from '@turf/turf';

import { Button } from "@/components/ui/button";

class Properties {
  parcel?: Parcel;
  poisChanged?: (pois: any[]) => void;
}

const numberFormat = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Kundenfarbe wie die frühere App.sass-Klasse `colorPrimary`, Fallback auf den Token. */
const PRIMARY_TEXT = "text-[color:var(--customer-primary,hsl(var(--foreground)))]";
const PRIMARY_LIGHT_TEXT = "text-[color:var(--customer-primary-light,hsl(var(--muted-foreground)))]";

export default function ParcelProperties(properties: Properties) {
  const [tab, setTab] = useState('general');
  const [_postalCode, setPostalCode] = useState() as any;
  const [area, setArea] = useState(0);

  const _title = () => {
    let name = properties.parcel?.name || 'Flurstück';

    name += ` (${properties.parcel?.landUse ? ` (${LandUseI18N['en'][properties.parcel.landUse]})` : ''})`;

    return name;
  }

  useEffect(() => {
    if (properties.parcel && properties.parcel.geometry && properties.parcel.geometry.coordinates &&
        properties.parcel.geometry.coordinates[0] && properties.parcel.geometry.coordinates[0][0]) {
      try {
        const originalCoords = properties.parcel.geometry.coordinates[0][0];
        console.log('Original coordinates:', originalCoords);

        // Create a GeoJSON polygon
        const polygon = turf.polygon([originalCoords]);
        console.log('Created polygon:', polygon);

        // Calculate area in square meters
        const area = turf.area(polygon);
        console.log('Calculated area:', area);

        // Convert to square meters (turf.area returns square meters)
        setArea(area);
      } catch (e) {
        console.warn('Could not calculate area for parcel:', e);
        setArea(0);
      }
    }
  }, [properties]);

  useEffect(() => {
    const call = async () => {
      if (!properties.parcel) {
        return;
      }

      try {
        const result = await userAPI.query(new Query(new EntityQuery(LocalGovernment.type, [], undefined, Filter.geoContainsBoundingBoxFilter(properties.parcel.minLat, properties.parcel.maxLat, properties.parcel.minLon, properties.parcel.maxLon))));

        console.log('Local Government', result);
      } catch (error) {
        console.error(error);
      }

      try {
        const result = await userAPI.query(new Query(new EntityQuery(PostalCode.type, [], undefined, Filter.geoContainsBoundingBoxFilter(properties.parcel.minLat, properties.parcel.maxLat, properties.parcel.minLon, properties.parcel.maxLon))));

        setPostalCode(result.length > 0 ? result[0] : undefined);

        console.log('Postal Code', result);
      } catch (error) {
        console.error(error);
      }

      try {
        const result = await userAPI.query(new Query(new EntityQuery(Building.type, [], undefined,
          Filter.geoBoundingBoxOverlapFilter(properties.parcel.minLat, properties.parcel.maxLat, properties.parcel.minLon, properties.parcel.maxLon))));

        console.log('Buildings', result);
      } catch (error) {
        console.error(error);
      }
    };

    call();
  }, [properties]);

  return (
    <div className="flex min-w-0 max-w-[1400px] flex-col gap-4">
      {/* Die frühere CommandBar trug nur „Allgemein“ (die übrigen Punkte sind im
          Bestand auskommentiert); als Werkzeugleiste aus ui/button. */}
      <div role="toolbar" aria-label="" className="flex flex-wrap items-center gap-1 border-b pb-1">
        <Button type="button" variant="ghost" size="sm" className="gap-2" onClick={() => setTab('general')}>
          <MapPin aria-hidden="true" className="h-4 w-4" />
          <span>Allgemein</span>
        </Button>
      </div>
      <div className="min-w-0">
        {(() => {
          switch (tab) {
            case 'general': return <div className="flex min-w-0 flex-col gap-1">
              <div className="text-lg">Nutzungsdetails</div>
              <div className={`text-2xl font-bold ${PRIMARY_TEXT}`}>{properties.parcel?.landUseDetails || '-'}</div>
              <div className="mt-4 text-lg">Fläche</div>
              <div className="flex flex-wrap items-baseline gap-1">
                <div className={`text-2xl font-bold ${PRIMARY_TEXT}`}>{numberFormat.format(area)}</div>
                <div className={`text-lg font-bold ${PRIMARY_TEXT}`}>&#13217;</div>
              </div>
            </div>
            case 'usage': return <div className="flex min-w-0 flex-col gap-1 text-base">
              <div>Gebäude</div>
              <div>Sport</div>
            </div>
            case 'topology': return <div className="flex min-w-0 flex-col gap-1 text-base">
              <div>Höhenprofil</div>
              <div>Boden</div>
              <div>Bewuchs</div>
            </div>
            case 'sociodemographics': return <SocioDemographicsProperties object={properties.parcel}></SocioDemographicsProperties>
            case 'poi': return <PoIProperties object={properties.parcel} poisChanged={properties.poisChanged}></PoIProperties>
            case 'weather': return <div className="min-w-0 text-base">Population</div>
            case 'crime': return <div className="flex min-w-0 flex-col gap-2">
              <div className="text-base">Kriminalität</div>
              <div className="flex flex-wrap items-baseline gap-2">
                <div className={`text-3xl font-bold ${PRIMARY_LIGHT_TEXT}`}>7.200</div>
                <div className={`text-2xl ${PRIMARY_LIGHT_TEXT}`}>Straftaten/100.000 Einwohner</div>
              </div>
              <div className="flex flex-wrap items-baseline gap-2">
                <div className={`text-2xl font-bold ${PRIMARY_TEXT}`}>3%</div>
                <div className={`text-lg ${PRIMARY_TEXT}`}>über Bundesdurchschnitt</div>
              </div>
              <div className="flex flex-wrap items-baseline gap-2">
                <div className={`text-2xl font-bold ${PRIMARY_TEXT}`}>5%</div>
                <div className={`text-lg ${PRIMARY_TEXT}`}>über Durchschnitt Hamburg</div>
              </div>
            </div>
            case 'ownership': return <div className="min-w-0 text-base">Eigentümer</div>
            case 'decarbonisation': return <div className="min-w-0">
              <DecarbonizationProperties object={properties.parcel}></DecarbonizationProperties>
            </div>
          }
        })()}
      </div>
    </div>
  );
}
